import { Descriptions as AntdDescriptions, Tag, Button, Card, Row, Col, Divider, List, Tooltip, Modal, Form, InputNumber, Input, message, Alert, theme, Collapse, Segmented } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ShopOutlined, PhoneOutlined, EnvironmentOutlined, ClockCircleOutlined, DollarOutlined, CreditCardOutlined, UserOutlined, CalendarOutlined, ApiOutlined, NodeIndexOutlined, ThunderboltOutlined, LineChartOutlined, HistoryOutlined, OrderedListOutlined, CompassOutlined } from "@ant-design/icons";
import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import TripContext from "../../../context/trip/context";
import TripContextProvider from "../../../context/trip/provider";
import {
  TripStatus,
  PaymentStatus,
  OrderTypePayment,
  getPaymentStatusColor,
  getPaymentStatusLabel,
  getStrategyLabel,
  getStrategyColor,
  IConfirmTripPaymentResponse,
} from "../../../models/trip/response";
import Space from "../../general/antd/space";
import TripOrderList from "../trip-order-list";
import TripRouteMap, { IMapOrder, IMapStore, RouteViewMode } from "../trip-route-map";
import { ITripOrderCardData } from "../trip-order-card";
import TripStatusTimeline from "../trip-status-timeline";
import { ILatLng, ILocation, calculateDistanceBetweenPoints, locationToLatLng } from "../../../utils/geo/haversine";
import { RouteStatus } from "../../general/map/route-polyline";
import { getOrderStatus, getOrderStatusColor } from "../../../models/order/enum";
import AuthContext from "../../../context/auth/context";
import { check } from "../../../utils/rbac/can";
import { Permissions } from "../../../utils/Permissions/Permissions";

interface Props { }

const { useToken } = theme;

const TripDetailsComponent: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { direction } = useContext(AppContext);
  const { grantedPermissions } = useContext(AuthContext);
  const { details, actions, loading } = useContext(TripContext);

  // Check permission for viewing route comparison details
  const canViewRouteComparison = check(grantedPermissions || [], Permissions.CAN_VIEW_ROUTE_COMPARISON);
  const { id } = useParams();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [routeViewMode, setRouteViewMode] = useState<RouteViewMode>("active");

  // Payment confirmation modal state
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentForm] = Form.useForm();
  const [paymentResult, setPaymentResult] = useState<IConfirmTripPaymentResponse | null>(null);

  // Calculate cash orders amount (expected amount)
  // Use totalOrderPrices from trip details directly
  const cashOrdersInfo = useMemo(() => {
    if (!details?.orders) return { count: 0, total: 0, electronicCount: 0 };

    const cashOrders = details.orders.filter(
      (order: any) => order.orderTypePayment === OrderTypePayment.CASH || order.orderTypePayment === "CASH"
    );
    const electronicOrders = details.orders.filter(
      (order: any) => order.orderTypePayment === OrderTypePayment.ELECTRONIC || order.orderTypePayment === "ELECTRONIC"
    );

    // Use totalOrderPrices from trip details as the expected amount
    const cashTotal = parseFloat(String(details.totalOrderPrices || 0)) || 0;

    return {
      count: cashOrders.length,
      total: cashTotal,
      electronicCount: electronicOrders.length,
    };
  }, [details?.orders, details?.totalOrderPrices]);

  // Transform trip orders to order list format
  // Handle both client object (from unassigned orders) and clientName string (from trip orders)
  const orderListData = useMemo((): ITripOrderCardData[] => {
    if (!details?.orders) return [];

    // Sort orders by sequence first for distance calculation
    const sortedOrders = [...details.orders].sort(
      (a: any, b: any) => (a.pivot?.sequence || 0) - (b.pivot?.sequence || 0)
    );

    // Get store location for calculating distance from store to first order
    const store = details?.store || details?.driver?.store;
    const storeLatLng = store?.location?.coordinates
      ? { lat: store.location.coordinates[1], lng: store.location.coordinates[0] }
      : undefined;

    return sortedOrders.map((order: any, index: number) => {
      // Build client object from clientName if client object is not available
      let client = order.client;
      if (!client && order.clientName) {
        // Split clientName into firstName and lastName
        const nameParts = order.clientName.trim().split(' ');
        client = {
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
        };
      }

      // Calculate distance from previous point
      let distanceFromPrevious = 0;
      if (order.location?.coordinates && order.location?.type) {
        const currentLocation = locationToLatLng(order.location as ILocation);

        if (index === 0) {
          // First order: calculate distance from store
          if (storeLatLng) {
            distanceFromPrevious = calculateDistanceBetweenPoints(storeLatLng, currentLocation);
          }
        } else {
          // Subsequent orders: calculate distance from previous order
          const prevOrder = sortedOrders[index - 1];
          if (prevOrder?.location?.coordinates && prevOrder?.location?.type) {
            const prevLocation = locationToLatLng(prevOrder.location as ILocation);
            distanceFromPrevious = calculateDistanceBetweenPoints(prevLocation, currentLocation);
          }
        }
      }

      return {
        id: order.id,
        number: order.number,
        status: getOrderStatus(Number(order.status)),
        statusColor: getOrderStatusColor(Number(order.status)),
        sequence: order.pivot?.sequence || 0,
        distanceToNext: order.pivot?.distanceToNext || 0,
        distanceFromPrevious,
        client,
        clientAddress: order.clientAddress,
      };
    });
  }, [details?.orders, details?.store, details?.driver?.store]);

  // Transform trip orders to map format
  // Handle both client object and clientName string
  const mapOrders = useMemo((): IMapOrder[] => {
    if (!details?.orders) return [];
    return details.orders
      .filter((order: any) => order.location?.coordinates)
      .map((order: any) => {
        // Use clientName directly if available, otherwise build from client object
        let clientName = order.clientName;
        if (!clientName && order.client) {
          clientName = `${order.client.firstName || ""} ${order.client.lastName || ""}`.trim();
        }

        return {
          id: order.id,
          number: order.number,
          status: getOrderStatus(Number(order.status)),
          sequence: order.pivot?.sequence || 0,
          distanceToNext: order.pivot?.distanceToNext || 0,
          clientName,
          clientAddress: order.clientAddress,
          location: {
            lat: order.location.coordinates[1],
            lng: order.location.coordinates[0],
          },
        };
      });
  }, [details?.orders]);

  // Get store location from driver's store (fallback for single store)
  const storeLocation = useMemo((): ILatLng | undefined => {
    const store = details?.store || details?.driver?.store;
    if (store?.location?.coordinates) {
      return {
        lat: store.location.coordinates[1],
        lng: store.location.coordinates[0],
      };
    }
    return undefined;
  }, [details?.store, details?.driver?.store]);

  // Transform trip stores to map format
  const mapStores = useMemo((): IMapStore[] => {
    if (!details?.stores) return [];
    return details.stores
      .filter((store) => store.location?.coordinates)
      .map((store) => ({
        id: store.id,
        name: store.name,
        logo: store.logo,
        location: {
          lat: store.location!.coordinates![1],
          lng: store.location!.coordinates![0],
        },
      }));
  }, [details?.stores]);

  // Get route status based on trip status
  const routeStatus = useMemo((): RouteStatus => {
    switch (details?.status) {
      case TripStatus.ACTIVE:
        return "active";
      case TripStatus.COMPLETED:
      case TripStatus.CANCELLED:
        return "completed"; // Both completed and cancelled show as finished routes
      default:
        return "pending";
    }
  }, [details?.status]);

  const handleOrderSelect = useCallback((orderId: number) => {
    setSelectedOrderId(orderId === selectedOrderId ? null : orderId);
    setSelectedStoreId(null); // Deselect store when selecting order
  }, [selectedOrderId]);

  // Handle store selection from card - pan to store location on map
  const handleStoreSelect = useCallback((store: any) => {
    if (store?.location?.coordinates && mapRef) {
      const lat = store.location.coordinates[1];
      const lng = store.location.coordinates[0];
      mapRef.panTo({ lat, lng });
      mapRef.setZoom(15);
      // Only toggle off if clicking the same store that's already selected
      // Otherwise always select the store
      setSelectedStoreId(selectedStoreId === store.id ? null : store.id);
      setSelectedOrderId(null); // Deselect order when selecting store
    }
  }, [mapRef, selectedStoreId]);

  // Handle store selection from map marker - find store by ID and select
  const handleMapStoreSelect = useCallback((storeId: number) => {
    const store = details?.stores?.find((s) => s.id === storeId);
    if (store) {
      handleStoreSelect(store);
    }
  }, [details?.stores, handleStoreSelect]);

  // Callback to receive map ref from TripRouteMap
  const handleMapLoad = useCallback((map: google.maps.Map) => {
    setMapRef(map);
  }, []);

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, [direction]);

  const getStatusTag = (status: TripStatus) => {
    switch (status) {
      case TripStatus.PENDING:
        return <Tag color="blue">{t("pending")}</Tag>;
      case TripStatus.ACTIVE:
        return <Tag color="green">{t("active")}</Tag>;
      case TripStatus.COMPLETED:
        return <Tag color="success">{t("completed")}</Tag>;
      case TripStatus.CANCELLED:
        return <Tag color="error">{t("cancelled")}</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const handleStartTrip = async () => {
    setActionLoading("start");
    const success = await actions.updateTrip(Number(id), { status: TripStatus.ACTIVE });
    if (success) {
      await actions.getDetails(Number(id));
    }
    setActionLoading(null);
  };

  const handleCompleteTrip = async () => {
    setActionLoading("complete");
    const success = await actions.updateTrip(Number(id), { status: TripStatus.COMPLETED });
    if (success) {
      await actions.getDetails(Number(id));
    }
    setActionLoading(null);
  };

  const handleCancelTrip = async () => {
    setActionLoading("cancel");
    const success = await actions.updateTrip(Number(id), {
      status: TripStatus.CANCELLED,
      reason: "Cancelled from admin panel",
    });
    if (success) {
      await actions.getDetails(Number(id));
    }
    setActionLoading(null);
  };

  const handleOpenPaymentModal = () => {
    paymentForm.setFieldsValue({
      amount_received: cashOrdersInfo.total,
      note: "",
    });
    setPaymentResult(null);
    setPaymentModalVisible(true);
  };

  const handleConfirmPayment = async () => {
    try {
      const values = await paymentForm.validateFields();
      setActionLoading("confirmPayment");

      const result = await actions.confirmPayment(Number(id), {
        amount_received: values.amount_received,
        note: values.note || undefined,
      });

      if (result) {
        setPaymentResult(result);

        // Refresh trip details to get updated payment status
        await actions.getDetails(Number(id));

        if (result.matched) {
          message.success(t("payment_confirmed_successfully"));
          setPaymentModalVisible(false);
        } else {
          message.warning(
            `${t("payment_confirmed_with_difference")}: ${Number(result.difference || 0).toFixed(2)} ${t("currency")}`
          );
          setPaymentModalVisible(false);
        }
      } else {
        message.error(t("payment_confirmation_failed"));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("trip")}
        buttonName={details?.status === TripStatus.PENDING ? t("update_trip") : undefined}
        onClick={
          details?.status === TripStatus.PENDING
            ? () => navigate(`/trips/update/${id}`)
            : undefined
        }
        loading={loading.includes("details")}
        onRefresh={() => actions.getDetails(Number(id))}
      >
        <div style={{ marginBottom: "1rem", display: "flex", gap: "0.5rem" }}>
          <Space>
            {details?.status === TripStatus.PENDING && (
              <Button
                type="primary"
                onClick={handleStartTrip}
                loading={actionLoading === "start"}
              >
                {t("start_trip")}
              </Button>
            )}
            {details?.status === TripStatus.ACTIVE && (
              <Tooltip
                title={
                  (details?.completedOrdersCount || 0) === 0
                    ? t("cannot_complete_trip_no_delivered_orders")
                    : undefined
                }
              >
                <Button
                  type="primary"
                  onClick={handleCompleteTrip}
                  loading={actionLoading === "complete"}
                  disabled={(details?.completedOrdersCount || 0) === 0}
                >
                  {t("complete_trip")}
                </Button>
              </Tooltip>
            )}
            {(details?.status === TripStatus.PENDING ||
              details?.status === TripStatus.ACTIVE) && (
                <Button
                  danger
                  onClick={handleCancelTrip}
                  loading={actionLoading === "cancel"}
                >
                  {t("cancel_trip")}
                </Button>
              )}
            {details?.status === TripStatus.COMPLETED &&
              details?.paymentStatus !== PaymentStatus.ACCEPTED && (
                <Button
                  type="primary"
                  style={{ backgroundColor: "#52c41a" }}
                  onClick={handleOpenPaymentModal}
                  loading={actionLoading === "confirmPayment"}
                >
                  {t("confirm_trip_payment")}
                </Button>
              )}
          </Space>
        </div>
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <>
          <Descriptions size="small" column={1} bordered>
            <AntdDescriptions.Item label={t("id")}>
              {details?.id}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("trip_number")}>
              {details?.number || "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("status")}>
              {details?.status && getStatusTag(details.status)}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("driver")}>
              {details?.driver
                ? `${details.driver.firstName} ${details.driver.lastName}`
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("store")}>
              {details?.driver?.store?.name ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("driver_classification")}>
              {details?.driver?.driverClassification?.name ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("orders_count")}>
              <Tooltip title={`${t("completed")}: ${details?.completedOrdersCount || 0} / ${details?.ordersCount || 0}`}>
                <span>
                  <CheckCircleOutlined style={{ color: "#52c41a", marginInlineEnd: 4 }} />
                  {details?.completedOrdersCount || 0}/{details?.ordersCount ?? details?.orders?.length ?? 0}
                </span>
              </Tooltip>
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("stores_count")}>
              <span>
                <ShopOutlined style={{ marginInlineEnd: 4 }} />
                {details?.storesCount || details?.stores?.length || 1}
              </span>
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("total_order_prices")}>
              {parseFloat(String(details?.totalOrderPrices || 0)).toFixed(2)} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("duration_for_payment")}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: 16 }}>
                  <ClockCircleOutlined style={{ marginInlineEnd: 4 }} />
                  {details?.durationForPayment ?? details?.estimatedDurationMinutes ?? 0} {t("minute_short")}
                </strong>
                <Tag color={details?.includesReturnTrip ? "blue" : "default"} style={{ margin: 0 }}>
                  {details?.includesReturnTrip ? t("includes_return") : t("without_return")}
                </Tag>
              </div>
            </AntdDescriptions.Item>

            {canViewRouteComparison && (
              <AntdDescriptions.Item label={t("duration_details")}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#666" }}>
                  <span>{t("without_return")}: {details?.estimatedDurationMinutes ?? 0} {t("minute_short")}</span>
                  <span>{t("with_return")}: {details?.estimatedDurationWithReturnMinutes ?? details?.estimatedDurationMinutes ?? 0} {t("minute_short")}</span>
                  {details?.actualDurationMinutes != null && (
                    <span style={{ color: "#52c41a" }}>
                      {t("actual_duration")}: {details.actualDurationMinutes} {t("minute_short")}
                    </span>
                  )}
                </div>
              </AntdDescriptions.Item>
            )}

            {/* Payment Confirmation Section - styled like Order */}
            <AntdDescriptions.Item label={t("payment_status")}>
              {details?.paymentReceivedBy || details?.paymentStatus === PaymentStatus.ACCEPTED ? (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  padding: "12px",
                  backgroundColor: token.colorSuccessBg,
                  border: `1px solid ${token.colorSuccessBorder}`,
                  borderRadius: token.borderRadius
                }}>
                  <Tag color="green" style={{ width: "fit-content" }}>
                    <CheckCircleOutlined style={{ marginInlineEnd: 4 }} />
                    {t("payment_received")}
                  </Tag>
                  {details?.paymentReceivedBy && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "8px" }}>
                      <span style={{ fontSize: "14px", fontWeight: "500", color: token.colorText }}>
                        <UserOutlined style={{ marginInlineEnd: 4, color: "#52c41a" }} />
                        <strong>{t("payment_received_by_accountant")}:</strong>{" "}
                        {`${details.paymentReceivedBy.firstName ?? ""} ${details.paymentReceivedBy.lastName ?? ""}`.trim() || `#${details.paymentReceivedById ?? details.paymentReceivedBy.id}`}
                      </span>
                      <span style={{
                        fontSize: "14px",
                        color: token.colorTextSecondary,
                        unicodeBidi: "plaintext"
                      }}>
                        <CalendarOutlined style={{ marginInlineEnd: 4, color: "#52c41a" }} />
                        <strong>{t("payment_received_at")}:</strong>{" "}
                        {details.paymentReceivedAt
                          ? new Date(details.paymentReceivedAt).toLocaleString()
                          : "-"}
                      </span>
                    </div>
                  )}
                </div>
              ) : details?.status === TripStatus.COMPLETED && details?.paymentStatus === PaymentStatus.PENDING ? (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  padding: "12px",
                  backgroundColor: token.colorWarningBg,
                  border: `1px solid ${token.colorWarningBorder}`,
                  borderRadius: token.borderRadius
                }}>
                  <Tag color="gold" style={{ width: "fit-content" }}>
                    <ClockCircleOutlined style={{ marginInlineEnd: 4 }} />
                    {t("payment_not_received")}
                  </Tag>
                  <span style={{ fontSize: "13px", color: token.colorTextSecondary }}>
                    {t("payment_pending_confirmation_message")}
                  </span>
                </div>
              ) : (
                <Tag color={details?.paymentStatus ? getPaymentStatusColor(details.paymentStatus) : "default"}>
                  {details?.paymentStatus ? t(getPaymentStatusLabel(details.paymentStatus)) : "-"}
                </Tag>
              )}
            </AntdDescriptions.Item>

            {/* Orders breakdown by payment type */}
            <AntdDescriptions.Item label={t("orders_payment_breakdown")}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <Tooltip title={t("cash_orders_tooltip")}>
                  <Tag color="green" style={{ margin: 0 }}>
                    <DollarOutlined /> {t("cash")}: {cashOrdersInfo.count} ({cashOrdersInfo.total.toFixed(2)} {t("currency")})
                  </Tag>
                </Tooltip>
                {cashOrdersInfo.electronicCount > 0 && (
                  <Tooltip title={t("electronic_orders_tooltip")}>
                    <Tag color="blue" style={{ margin: 0 }}>
                      <CreditCardOutlined /> {t("electronic")}: {cashOrdersInfo.electronicCount} ({t("already_paid")})
                    </Tag>
                  </Tooltip>
                )}
              </div>
            </AntdDescriptions.Item>

            {/* Payment Receipt Details - shown only after payment confirmation */}
            {details?.actualAmountReceived != null && (
              <AntdDescriptions.Item label={t("payment_receipt_details")}>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  padding: "12px",
                  backgroundColor: token.colorInfoBg,
                  border: `1px solid ${token.colorInfoBorder}`,
                  borderRadius: token.borderRadius
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{t("expected_collected_from_clients")}:</span>
                    <strong>{parseFloat(String(details?.expectedCollectedFromClients || 0)).toFixed(2)} {t("currency")}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{t("actual_amount_received")}:</span>
                    <strong>{parseFloat(String(details?.actualAmountReceived || 0)).toFixed(2)} {t("currency")}</strong>
                  </div>
                  {details?.expectedCollectedFromClients != null && details?.actualAmountReceived != null && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>{t("difference")}:</span>
                      {(() => {
                        const diff = parseFloat(String(details.actualAmountReceived || 0)) - parseFloat(String(details.expectedCollectedFromClients || 0));
                        const color = diff > 0 ? "#388e3c" : diff < 0 ? "#d32f2f" : "#757575";
                        return (
                          <Tag color={diff > 0 ? "green" : diff < 0 ? "red" : "default"} style={{ margin: 0 }}>
                            {diff > 0 ? "+" : ""}{diff.toFixed(2)} {t("currency")}
                          </Tag>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </AntdDescriptions.Item>
            )}

            <AntdDescriptions.Item label={t("distance_for_payment")}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: 16 }}>
                  {parseFloat(String(details?.routeStats?.distanceSummary?.activeForPayment ?? details?.distanceForPayment ?? details?.totalDistance ?? 0)).toFixed(2)} {t("km")}
                </strong>
                <Tag color={details?.routeStats?.distanceSummary?.paymentIncludesReturn ?? details?.includesReturnTrip ? "blue" : "default"} style={{ margin: 0 }}>
                  {(details?.routeStats?.distanceSummary?.paymentIncludesReturn ?? details?.includesReturnTrip) ? t("includes_return") : t("without_return")}
                </Tag>
              </div>
            </AntdDescriptions.Item>

            {canViewRouteComparison && (
              <AntdDescriptions.Item label={t("distance_summary")}>
                {details?.routeStats?.distanceSummary ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#666" }}>
                      <EnvironmentOutlined style={{ color: "#3458a4" }} />
                      <span>{t("without_return")}: <strong>{parseFloat(String(details.routeStats.distanceSummary.withoutReturn)).toFixed(2)} {t("km")}</strong></span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#666" }}>
                      <NodeIndexOutlined style={{ color: "#13a8a8" }} />
                      <span>{t("with_return")}: <strong>{parseFloat(String(details.routeStats.distanceSummary.withReturn)).toFixed(2)} {t("km")}</strong></span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#666" }}>
                      <span style={{ fontSize: 14 }}>↩️</span>
                      <span>{t("return_segment_distance")}: <strong>{parseFloat(String(details.routeStats.distanceSummary.returnSegmentOnly)).toFixed(2)} {t("km")}</strong></span>
                    </div>
                    <div style={{ borderTop: "1px dashed #d9d9d9", paddingTop: 6, marginTop: 2 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                        <span style={{ color: "#52c41a", fontWeight: 600 }}>
                          {t("active_for_payment")}: {parseFloat(String(details.routeStats.distanceSummary.activeForPayment)).toFixed(2)} {t("km")}
                        </span>
                        <Tag color={details.routeStats.distanceSummary.paymentIncludesReturn ? "blue" : "default"} style={{ margin: 0 }}>
                          {details.routeStats.distanceSummary.paymentIncludesReturn ? t("includes_return") : t("without_return")}
                        </Tag>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#666" }}>
                    <span>{t("without_return")}: {parseFloat(String(details?.totalDistance || 0)).toFixed(2)} {t("km")}</span>
                    <span>{t("with_return")}: {parseFloat(String(details?.totalDistanceWithReturn ?? details?.totalDistance ?? 0)).toFixed(2)} {t("km")}</span>
                  </div>
                )}
              </AntdDescriptions.Item>
            )}

            <AntdDescriptions.Item label={t("base_cost")}>
              {parseFloat(String(details?.baseCost || 0)).toFixed(2)} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("addition_cost")}>
              {parseFloat(String(details?.bonusCost || 0)).toFixed(2)} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("cost_for_payment")}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: 16, color: "#52c41a" }}>
                  {parseFloat(String(details?.costForPayment ?? details?.totalCost ?? 0)).toFixed(2)} {t("currency")}
                </strong>
                <Tag color={details?.includesReturnTrip ? "blue" : "default"} style={{ margin: 0 }}>
                  {details?.includesReturnTrip ? t("includes_return") : t("without_return")}
                </Tag>
              </div>
            </AntdDescriptions.Item>

            {canViewRouteComparison && (
              <AntdDescriptions.Item label={t("cost_details")}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#666" }}>
                  <span>{t("without_return")}: {parseFloat(String(details?.totalCost || 0)).toFixed(2)} {t("currency")}</span>
                  <span>{t("with_return")}: {parseFloat(String(details?.totalCostWithReturn ?? details?.totalCost ?? 0)).toFixed(2)} {t("currency")}</span>
                </div>
              </AntdDescriptions.Item>
            )}

            {/* Route Strategy Info */}
            {details?.strategyName && (
              <AntdDescriptions.Item label={t("route_strategy")}>
                <Tag
                  color={getStrategyColor(details.strategyName)}
                  icon={<ThunderboltOutlined />}
                >
                  {t(getStrategyLabel(details.strategyName))}
                </Tag>
              </AntdDescriptions.Item>
            )}

            {details?.mapPolylines && (
              <AntdDescriptions.Item label={t("route_segments")}>
                <NodeIndexOutlined style={{ marginInlineEnd: 4 }} />
                {details.mapPolylines.totalSegments} {t("segments")}
              </AntdDescriptions.Item>
            )}

            <AntdDescriptions.Item label={t("calculating_delivery_cost_per_kilometer")}>
              {details?.calculatingDeliveryCostPerKilometer ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {t("yes")}
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="default">
                  {t("no")}
                </Tag>
              )}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("delivery_cost_per_kilometer")}>
              {details?.deliveryCostPerKilometer} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("fixed_order_delivery_cost")}>
              {details?.orderDeliveryCost} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("extra_order_fixed_bonus_enabled")}>
              {details?.extraOrderFixedBonusEnabled ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {t("yes")}
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="default">
                  {t("no")}
                </Tag>
              )}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("extra_order_fixed_bonus_amount")}>
              {details?.extraOrderFixedBonusAmount} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("started_at")}>
              {details?.startedAt
                ? new Date(details.startedAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("completed_at")}>
              {details?.completedAt
                ? new Date(details.completedAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("cancelled_at")}>
              {details?.cancelledAt
                ? new Date(details.cancelledAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("cancellation_reason")}>
              {details?.cancellationReason ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("created_at")}>
              {details?.createdAt
                ? new Date(details.createdAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("created_by")}>
              {details?.createdBy
                ? `${details.createdBy.firstName ?? ""} ${details.createdBy.lastName ?? ""}`.trim() || "-"
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("updated_at")}>
              {details?.updatedAt
                ? new Date(details.updatedAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("updated_by")}>
              {details?.updatedBy
                ? `${details.updatedBy.firstName ?? ""} ${details.updatedBy.lastName ?? ""}`.trim() || "-"
                : "-"}
            </AntdDescriptions.Item>
          </Descriptions>

          {/* Status History Timeline - Collapsible */}
          {details?.statusHistory && details.statusHistory.length > 0 && (
            <Collapse
              size="small"
              style={{ marginTop: 16, marginBottom: 16 }}
              items={[
                {
                  key: "status-history",
                  label: (
                    <span>
                      <HistoryOutlined style={{ marginInlineEnd: 8 }} />
                      {t("status_history")} ({details.statusHistory.length})
                    </span>
                  ),
                  children: (
                    <TripStatusTimeline statusHistory={details.statusHistory} />
                  ),
                },
              ]}
            />
          )}

          {/* Routes Comparison - only when data exists AND user has permission */}
          {canViewRouteComparison && details?.routesComparison && (
            <>
              <Divider orientation={direction === "rtl" ? "right" : "left"}>
                {t("route_comparison")}
              </Divider>
              <Card size="small" style={{ marginBottom: 16 }}>
                {/* NEW FORMAT: 3-strategy comparison when comparisonEnabled is true */}
                {details.routesComparison.comparisonEnabled && details.routesComparison.ranking ? (
                  <>
                    {/* Warning if active strategy is not optimal */}
                    {details.routesComparison.activeStrategy && !details.routesComparison.activeStrategy.isOptimal && (
                      <Alert
                        type="warning"
                        message={t("active_not_optimal_warning")}
                        description={
                          details.routesComparison.distanceSavings?.[details.routesComparison.activeStrategy.key]
                            ? `${t("potential_savings")}: ${(details.routesComparison.distanceSavings[details.routesComparison.activeStrategy.key].savedKm ?? 0).toFixed(2)} ${t("km")} (${(details.routesComparison.distanceSavings[details.routesComparison.activeStrategy.key].savedPercentage ?? 0).toFixed(1)}%)`
                            : undefined
                        }
                        showIcon
                        style={{ marginBottom: 12 }}
                      />
                    )}
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid rgba(255,255,255,0.1)" }}>
                            <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, color: "#888", width: 60 }}>
                              {t("strategy_rank")}
                            </th>
                            <th style={{ padding: "10px 8px", textAlign: direction === "rtl" ? "right" : "left", fontWeight: 600, color: "#888" }}>
                              {t("route_strategy")}
                            </th>
                            <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, color: "#888" }}>
                              {t("distance")}
                            </th>
                            <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, color: "#888" }}>
                              {t("potential_savings")}
                            </th>
                            <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, color: "#888" }}>
                              {t("status")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.routesComparison.ranking.map((item) => {
                            const strategy = details.routesComparison?.strategies?.[item.key];
                            const savings = details.routesComparison?.distanceSavings?.[item.key];
                            const isActive = strategy?.isActive;
                            const isShortest = savings?.isShortest;

                            // Strategy colors
                            const strategyColors: Record<string, string> = {
                              route_optimization: "#fa8c16",
                              hybrid_shortest: "#722ed1",
                              nearest_neighbor_shortest: "#faad14",
                            };
                            const color = strategyColors[item.key] || "#888";

                            return (
                              <tr
                                key={item.key}
                                style={{
                                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                                  backgroundColor: isActive ? "rgba(82, 196, 26, 0.08)" : undefined,
                                }}
                              >
                                <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, color: "#888" }}>
                                  #{item.rank}
                                </td>
                                <td style={{ padding: "10px 8px", textAlign: direction === "rtl" ? "right" : "left" }}>
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
                                    <span style={{ color, fontWeight: 600 }}>
                                      {direction === "rtl" ? item.nameAr : item.name}
                                    </span>
                                  </span>
                                </td>
                                <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, color }}>
                                  {(item.totalDistance ?? 0).toFixed(2)} {t("km")}
                                </td>
                                <td style={{ padding: "10px 8px", textAlign: "center" }}>
                                  {isShortest ? (
                                    <Tag color="blue" icon={<CheckCircleOutlined />}>
                                      {t("shortest_route")}
                                    </Tag>
                                  ) : savings ? (
                                    <span style={{ color: "#52c41a", fontWeight: 500 }}>
                                      -{(savings.savedKm ?? 0).toFixed(2)} {t("km")}
                                      <span style={{ fontSize: 11, marginInlineStart: 4, color: "#888" }}>
                                        (-{(savings.savedPercentage ?? 0).toFixed(1)}%)
                                      </span>
                                    </span>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                                <td style={{ padding: "10px 8px", textAlign: "center" }}>
                                  {isActive && (
                                    <Tag color="green" icon={<CheckCircleOutlined />}>
                                      {t("active_strategy")}
                                    </Tag>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : details.routesComparison.originalRoute && details.routesComparison.shortestRoute ? (
                  /* LEGACY FORMAT: 2-route comparison table */
                  (() => {
                    // Calculate return distance for each route from their polylines (last segment with is_return flag)
                    const getReturnDistanceFromPolylines = (polylines: any[] | undefined): number => {
                      if (!polylines?.length) return 0;
                      // Find segment with is_return flag, or use last segment
                      const returnSegment = polylines.find((p: any) => p.is_return)
                        || [...polylines].sort((a, b) => a.index - b.index).pop();
                      if (!returnSegment) return 0;
                      // Handle both snake_case and camelCase (API inconsistency)
                      const distanceMeters = returnSegment.distance_meters ?? returnSegment.distanceMeters ?? 0;
                      return distanceMeters / 1000;
                    };

                    const optimizedReturnDistance = getReturnDistanceFromPolylines(details.routesComparison!.originalRoute!.polylines);
                    const shortestReturnDistance = getReturnDistanceFromPolylines(details.routesComparison!.shortestRoute!.polylines);

                    const optimizedWithReturn = parseFloat(String(details.routesComparison!.originalRoute!.total_distance || 0)) || 0;
                    const shortestWithReturn = parseFloat(String(details.routesComparison!.shortestRoute!.total_distance || 0)) || 0;
                    const optimizedWithoutReturn = optimizedWithReturn - optimizedReturnDistance;
                    const shortestWithoutReturn = shortestWithReturn - shortestReturnDistance;

                    // Calculate differences and percentages
                    const withoutReturnDiff = Math.abs(optimizedWithoutReturn - shortestWithoutReturn);
                    const withoutReturnPercent = optimizedWithoutReturn > 0
                      ? (withoutReturnDiff / optimizedWithoutReturn) * 100
                      : 0;

                    const returnDiff = Math.abs(optimizedReturnDistance - shortestReturnDistance);
                    const returnPercent = optimizedReturnDistance > 0
                      ? (returnDiff / optimizedReturnDistance) * 100
                      : 0;

                    return (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr style={{ borderBottom: "2px solid rgba(255,255,255,0.1)" }}>
                              <th style={{ padding: "10px 8px", textAlign: direction === "rtl" ? "right" : "left", fontWeight: 600, color: "#888" }}></th>
                              <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600 }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#fa8c16" }} />
                                  <span style={{ color: "#fa8c16" }}>{t("strategy_optimized")}</span>
                                  {details.strategyUsed === 1 && (
                                    <Tag color="green" style={{ margin: 0, fontSize: 10 }} icon={<CheckCircleOutlined />}>
                                      {t("active_strategy")}
                                    </Tag>
                                  )}
                                </span>
                              </th>
                              <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600 }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#722ed1" }} />
                                  <span style={{ color: "#722ed1" }}>{t("strategy_hybrid_shortest")}</span>
                                  {details.strategyUsed === 2 && (
                                    <Tag color="green" style={{ margin: 0, fontSize: 10 }} icon={<CheckCircleOutlined />}>
                                      {t("active_strategy")}
                                    </Tag>
                                  )}
                                </span>
                              </th>
                              <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, color: "#52c41a" }}>
                                <LineChartOutlined style={{ marginInlineEnd: 4 }} />
                                {t("distance_difference")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Without Return Row */}
                            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                              <td style={{ padding: "10px 8px", fontWeight: 500, color: "#888" }}>
                                <EnvironmentOutlined style={{ marginInlineEnd: 6, color: "#3458a4" }} />
                                {t("without_return")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#fa8c16", fontWeight: 600 }}>
                                {optimizedWithoutReturn.toFixed(3)} {t("km")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#722ed1", fontWeight: 600 }}>
                                {shortestWithoutReturn.toFixed(3)} {t("km")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#52c41a", fontWeight: 500 }}>
                                {withoutReturnDiff.toFixed(3)} {t("km")}
                                <span style={{ fontSize: 11, marginInlineStart: 4, color: "#888" }}>
                                  ({withoutReturnPercent.toFixed(1)}%)
                                </span>
                              </td>
                            </tr>
                            {/* Return Segment Row */}
                            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                              <td style={{ padding: "10px 8px", fontWeight: 500, color: "#888" }}>
                                <span style={{ marginInlineEnd: 6 }}>↩️</span>
                                {t("return_segment_distance")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#fa8c16", fontWeight: 600 }}>
                                {optimizedReturnDistance.toFixed(3)} {t("km")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#722ed1", fontWeight: 600 }}>
                                {shortestReturnDistance.toFixed(3)} {t("km")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#52c41a", fontWeight: 500 }}>
                                {returnDiff.toFixed(3)} {t("km")}
                                <span style={{ fontSize: 11, marginInlineStart: 4, color: "#888" }}>
                                  ({returnPercent.toFixed(1)}%)
                                </span>
                              </td>
                            </tr>
                            {/* With Return Row (Total) */}
                            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                              <td style={{ padding: "10px 8px", fontWeight: 500, color: "#888" }}>
                                <NodeIndexOutlined style={{ marginInlineEnd: 6, color: "#13a8a8" }} />
                                {t("with_return")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#fa8c16", fontWeight: 600 }}>
                                {optimizedWithReturn.toFixed(3)} {t("km")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#722ed1", fontWeight: 600 }}>
                                {shortestWithReturn.toFixed(3)} {t("km")}
                              </td>
                              <td style={{ padding: "10px 8px", textAlign: "center", color: "#52c41a", fontWeight: 500 }}>
                                {Math.abs(parseFloat(String(details.routesComparison!.difference?.distanceSavedKm || 0))).toFixed(3)} {t("km")}
                                <span style={{ fontSize: 11, marginInlineStart: 4, color: "#888" }}>
                                  ({Math.abs(parseFloat(String(details.routesComparison!.difference?.percentageSaved || 0))).toFixed(1)}%)
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()
                ) : null}
              </Card>
            </>
          )}

          {/* Route Calculation Stats - Collapsible - only with permission */}
          {canViewRouteComparison && details?.routeStats && (details.routeStats.apiCalls || details.routeStats.lastCalculation || details.routeStats.calculationCount > 0) && (
            <Collapse
              size="small"
              style={{ marginBottom: 16 }}
              items={[
                {
                  key: "route-stats",
                  label: (
                    <span>
                      <ApiOutlined style={{ marginInlineEnd: 8 }} />
                      {t("route_calculation_stats")}
                    </span>
                  ),
                  children: (
                    <Descriptions size="small" column={2} bordered>
                      <AntdDescriptions.Item label={t("total_calculations")}>
                        {details.routeStats.calculationCount}
                      </AntdDescriptions.Item>
                      {details.routeStats.apiCalls && (
                        <>
                          <AntdDescriptions.Item label={t("total_api_calls")}>
                            {details.routeStats.apiCalls.total_api_calls}
                          </AntdDescriptions.Item>
                          <AntdDescriptions.Item label={t("route_optimization_calls")}>
                            {details.routeStats.apiCalls.total_route_optimization_calls}
                          </AntdDescriptions.Item>
                          <AntdDescriptions.Item label={t("routes_api_calls")}>
                            {details.routeStats.apiCalls.total_routes_api_calls}
                          </AntdDescriptions.Item>
                        </>
                      )}
                      {details.routeStats.lastCalculation && (
                        <>
                          <AntdDescriptions.Item label={t("last_calculation_at")}>
                            {new Date(details.routeStats.lastCalculation.calculated_at).toLocaleString()}
                          </AntdDescriptions.Item>
                          <AntdDescriptions.Item label={t("route_strategy")}>
                            <Tag
                              color={getStrategyColor(details.strategyName)}
                              icon={<ThunderboltOutlined />}
                            >
                              {t(getStrategyLabel(details.strategyName))}
                            </Tag>
                          </AntdDescriptions.Item>
                        </>
                      )}
                    </Descriptions>
                  ),
                },
              ]}
            />
          )}

          {/* Stores Section - Collapsible */}
          {details?.stores && details.stores.length > 0 && (
            <Collapse
              size="small"
              style={{ marginBottom: 16 }}
              items={[
                {
                  key: "trip-stores",
                  label: (
                    <span>
                      <ShopOutlined style={{ marginInlineEnd: 8 }} />
                      {t("trip_stores")} ({details.stores.length})
                    </span>
                  ),
                  children: (
                    <Row gutter={[16, 16]}>
                      {details.stores.map((store) => (
                        <Col xs={24} sm={12} lg={8} key={store.id}>
                          <Card
                            size="small"
                            hoverable
                            onClick={() => handleStoreSelect(store)}
                            style={{
                              cursor: store.location?.coordinates ? "pointer" : "default",
                              borderColor: selectedStoreId === store.id ? "#1890ff" : undefined,
                              boxShadow: selectedStoreId === store.id ? "0 0 0 2px rgba(24,144,255,0.2)" : undefined,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 8,
                                  backgroundColor: store.logo ? "transparent" : (selectedStoreId === store.id ? "#1890ff" : "#f0f5ff"),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "background-color 0.3s",
                                  border: selectedStoreId === store.id ? "2px solid #1890ff" : "none",
                                  overflow: "hidden",
                                }}
                              >
                                {store.logo && store.logo.length > 0 ? (
                                  <img
                                    src={store.logo}
                                    alt={store.name}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 6,
                                    }}
                                  />
                                ) : (
                                  <ShopOutlined style={{ fontSize: 20, color: selectedStoreId === store.id ? "#fff" : "#1890ff" }} />
                                )}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>{store.name}</div>
                                {store.phone && (
                                  <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>
                                    <PhoneOutlined style={{ marginInlineEnd: 4 }} />
                                    {store.phone}
                                  </div>
                                )}
                                {store.address && (
                                  <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>
                                    <EnvironmentOutlined style={{ marginInlineEnd: 4 }} />
                                    {store.address}
                                  </div>
                                )}
                                <div style={{ marginTop: 8 }}>
                                  <Tag color="blue">{store.productsCount || 0} {t("products")}</Tag>
                                  <Tag color="green">{parseFloat(String(store.productsTotalValue || 0)).toFixed(2)} {t("currency")}</Tag>
                                  {store.orders && (
                                    <Tag>{store.orders.length} {t("orders")}</Tag>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ),
                },
              ]}
            />
          )}

          {/* Trip Orders Section - Collapsible */}
          {details?.orders && details.orders.length > 0 && (
            <Collapse
              size="small"
              style={{ marginBottom: 16 }}
              defaultActiveKey={["trip-orders"]}
              items={[
                {
                  key: "trip-orders",
                  label: (
                    <span>
                      <OrderedListOutlined style={{ marginInlineEnd: 8 }} />
                      {t("trip_orders")} ({details.orders.length})
                    </span>
                  ),
                  children: (
                    <div style={{ maxHeight: 400, overflowY: "auto" }}>
                      <TripOrderList
                        orders={orderListData}
                        selectedOrderId={selectedOrderId}
                        isEditable={false}
                        onOrderSelect={handleOrderSelect}
                      />
                    </div>
                  ),
                },
              ]}
            />
          )}

          {/* Route Map Section - Collapsible */}
          {details?.orders && details.orders.length > 0 && (
            <Collapse
              size="small"
              style={{ marginBottom: 16 }}
              defaultActiveKey={["route-map"]}
              items={[
                {
                  key: "route-map",
                  label: (
                    <span>
                      <CompassOutlined style={{ marginInlineEnd: 8 }} />
                      {t("route_map")}
                    </span>
                  ),
                  children: (
                    <div style={{ height: 650 }}>
                      {/* Route comparison toggle - only show when has permission AND comparison data available */}
                      {canViewRouteComparison && details?.routesComparison && (
                        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 500 }}>{t("compare_routes")}:</span>
                          <Segmented
                            value={routeViewMode}
                            onChange={(value) => setRouteViewMode(value as RouteViewMode)}
                            options={
                              details.routesComparison.comparisonEnabled
                                ? [
                                    {
                                      label: `${t("show_current_route")} (${t(getStrategyLabel(details.strategyName))})`,
                                      value: "active"
                                    },
                                    { label: t("show_optimized_route"), value: "optimized" },
                                    { label: t("show_shortest_route"), value: "shortest" },
                                    { label: t("strategy_nearest_neighbor"), value: "nearest_neighbor" },
                                    { label: t("show_both_routes"), value: "all" },
                                  ]
                                : [
                                    {
                                      label: `${t("show_current_route")} (${t(getStrategyLabel(details.strategyName))})`,
                                      value: "active"
                                    },
                                    { label: t("show_optimized_route"), value: "optimized" },
                                    { label: t("show_shortest_route"), value: "shortest" },
                                    { label: t("show_both_routes"), value: "all" },
                                  ]
                            }
                          />
                        </div>
                      )}
                      <TripRouteMap
                        storeLocation={storeLocation}
                        stores={mapStores}
                        selectedStoreId={selectedStoreId}
                        assignedOrders={mapOrders}
                        selectedOrderId={selectedOrderId}
                        routeStatus={routeStatus}
                        isEditable={false}
                        onStoreSelect={handleMapStoreSelect}
                        onOrderSelect={handleOrderSelect}
                        onMapLoad={handleMapLoad}
                        mapPolylines={details?.mapPolylines}
                        routesComparison={details?.routesComparison}
                        routeViewMode={routeViewMode}
                      />
                    </div>
                  ),
                },
              ]}
            />
          )}
          </>
        )}
      </PageDetails>

      {/* Payment Confirmation Modal */}
      <Modal
        title={t("confirm_payment_receipt")}
        open={paymentModalVisible}
        onCancel={() => {
          setPaymentModalVisible(false);
          setPaymentResult(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setPaymentModalVisible(false)}>
            {t("cancel")}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            style={{ backgroundColor: "#52c41a" }}
            onClick={handleConfirmPayment}
            loading={actionLoading === "confirmPayment"}
          >
            {t("confirm_payment_action")}
          </Button>,
        ]}
        width={500}
      >
        {/* Orders Summary */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("orders_payment_summary")}
          description={
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              <div>
                <strong>{t("total_orders")}:</strong> {details?.orders?.length || 0}
              </div>
              <div style={{ color: "#52c41a" }}>
                <DollarOutlined /> <strong>{t("cash_orders")}:</strong> {cashOrdersInfo.count} ({cashOrdersInfo.total.toFixed(2)} {t("currency")})
              </div>
              {cashOrdersInfo.electronicCount > 0 && (
                <div style={{ color: "#1890ff" }}>
                  <CreditCardOutlined /> <strong>{t("electronic_orders")}:</strong> {cashOrdersInfo.electronicCount} ({t("already_paid")})
                </div>
              )}
            </div>
          }
        />

        {/* Orders List */}
        {details?.orders && details.orders.length > 0 && (
          <Collapse
            size="small"
            style={{ marginBottom: 16 }}
            items={[
              {
                key: "1",
                label: `${t("trip_orders")} (${details.orders.length})`,
                children: (
                  <List
                    size="small"
                    dataSource={details.orders || []}
                    renderItem={(order: any) => {
                      const isCash = order.orderTypePayment === OrderTypePayment.CASH || order.orderTypePayment === "CASH";
                      const isElectronic = order.orderTypePayment === OrderTypePayment.ELECTRONIC || order.orderTypePayment === "ELECTRONIC";
                      const orderStatus = typeof order.status === "number" ? order.status : parseInt(order.status, 10);
                      const isDelivered = orderStatus === 8; // COMPLETED_AND_PAID
                      const isCancelled = [9, 10, 11].includes(orderStatus); // CANCELLED_BY_*
                      const isSkipped = orderStatus === 12; // EMERGENCY_BY_DELIVERY_DRIVER (skipped)

                      return (
                        <List.Item style={{
                          backgroundColor: isCancelled ? "rgba(255, 77, 79, 0.1)" : isSkipped ? "rgba(250, 173, 20, 0.1)" : "transparent",
                          borderRadius: 4,
                          padding: "8px 12px",
                          marginBottom: 4,
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                            <span style={{
                              fontWeight: isDelivered ? 600 : 400,
                              color: isCancelled ? "#ff4d4f" : isSkipped ? "#faad14" : "inherit",
                              textDecoration: isCancelled ? "line-through" : "none",
                            }}>
                              #{order.number || order.id}
                              {isDelivered && <CheckCircleOutlined style={{ color: "#52c41a", marginInlineStart: 6 }} />}
                              {isCancelled && <CloseCircleOutlined style={{ color: "#ff4d4f", marginInlineStart: 6 }} />}
                            </span>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              {/* Order Status Tag */}
                              {isDelivered && (
                                <Tag color="success">
                                  {t("delivered")}
                                </Tag>
                              )}
                              {isCancelled && (
                                <Tag color="error">
                                  {t("cancelled")}
                                </Tag>
                              )}
                              {isSkipped && (
                                <Tag color="warning">
                                  {t("skipped")}
                                </Tag>
                              )}
                              {/* Payment Type Tag */}
                              {isCash && (
                                <Tag color="green" icon={<DollarOutlined />}>
                                  {t("cash")}
                                </Tag>
                              )}
                              {isElectronic && (
                                <Tag color="blue" icon={<CreditCardOutlined />}>
                                  {t("electronic")}
                                </Tag>
                              )}
                            </div>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                ),
              },
            ]}
          />
        )}

        <Form form={paymentForm} layout="vertical">
          <Form.Item
            label={t("expected_amount")}
          >
            <InputNumber
              style={{ width: "100%" }}
              value={cashOrdersInfo.total}
              disabled
              addonAfter={t("currency")}
            />
          </Form.Item>

          <Form.Item
            name="amount_received"
            label={t("amount_received")}
            rules={[
              { required: true, message: t("field_is_required_message") },
              { type: "number", min: 0, message: t("min_value_zero") },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="0.00"
              addonAfter={t("currency")}
              precision={2}
            />
          </Form.Item>

          {/* Show difference */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const amountReceived = getFieldValue("amount_received") || 0;
              const difference = amountReceived - cashOrdersInfo.total;
              if (difference !== 0) {
                return (
                  <Alert
                    type={difference > 0 ? "success" : "warning"}
                    style={{ marginBottom: 16 }}
                    message={
                      <span>
                        {t("difference")}: <strong>{difference.toFixed(2)} {t("currency")}</strong>
                        {difference > 0 ? ` (${t("excess")})` : ` (${t("deficit")})`}
                      </span>
                    }
                  />
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="note"
            label={t("note")}
          >
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder={t("optional")}
              showCount
            />
          </Form.Item>
        </Form>

        {/* Show result after confirmation */}
        {paymentResult && (
          <Alert
            type={paymentResult.matched ? "success" : "warning"}
            showIcon
            message={paymentResult.matched ? t("payment_matched") : t("payment_not_matched")}
            description={
              <div style={{ marginTop: 8 }}>
                <div>{t("expected_amount")}: {Number(paymentResult.expected_amount ?? 0).toFixed(2)} {t("currency")}</div>
                <div>{t("amount_received")}: {Number(paymentResult.amount_received ?? 0).toFixed(2)} {t("currency")}</div>
                <Divider style={{ margin: "8px 0" }} />
                <div style={{ color: (paymentResult.difference ?? 0) < 0 ? "#d32f2f" : (paymentResult.difference ?? 0) > 0 ? "#388e3c" : undefined }}>
                  {t("difference")}: {(paymentResult.difference ?? 0) > 0 ? "+" : ""}{Number(paymentResult.difference ?? 0).toFixed(2)} {t("currency")}
                </div>
              </div>
            }
          />
        )}
      </Modal>
    </>
  );
};

const TripDetailsPage = () => {
  return (
    <TripContextProvider>
      <TripDetailsComponent />
    </TripContextProvider>
  );
};

export default TripDetailsPage;
